<script setup lang="ts">
import { useRouter } from 'vue-router'
import { VButton } from '@/modules/shared/components'
import { ArrowRight } from 'lucide-vue-next'
import OrbitalAnimation from './orbital-animation.vue'

const router = useRouter()

const startConnecting = () => {
  // Always route to the profile review step so the admin can verify
  // their profile before opting in. The setting is enabled on save.
  router.push({ name: 'connecting.groups.edit' })
}

const STEPS = [
  {
    number: '1',
    icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    title: 'Set up your firm profile',
    description: 'Describe your fund, structure, and investor criteria so we can find the right matches.',
  },
  {
    number: '2',
    icon: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35',
    title: 'Review your matches',
    description: 'We\u2019ll surface qualified investors that align with your fund and investment thesis.',
  },
  {
    number: '3',
    icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3',
    title: 'Approve & connect',
    description:
      'Investors can\u2019t see your firm until you approve them. Once approved, they\u2019ll receive an invitation to connect.',
  },
]
</script>

<template>
  <div class="welcome-fullbleed">
    <!-- Full-bleed background with hero + animation -->
    <div
      class="grid min-h-full grid-cols-1 sm:h-full sm:min-h-0 sm:overflow-y-auto lg:grid-cols-2"
      style="background: #162a3a"
    >
      <!-- Left Content -->
      <div class="flex flex-col justify-center px-6 py-6 sm:px-10 sm:py-8 sm:pb-60 lg:px-14 lg:py-10 lg:pb-56">
        <h1
          class="title mb-4 text-balance text-3xl font-bold leading-[1.1] tracking-wide text-white sm:text-4xl lg:text-[42px]"
        >
          Qualified investors, aligned with your <span class="italic text-[#3b88af]">firm</span>
        </h1>

        <p class="subtitle mb-8 max-w-[480px] text-base leading-relaxed text-gray-300 lg:text-lg">
          Complete your profile and we'll surface investor matches for you to review. You choose who to invite —
          investors only see your firm when you approve them.
        </p>

        <div class="cta-wrap">
          <VButton size="2xl" variant="v-blue" :click="startConnecting">
            <span class="flex items-center gap-2">
              Review Profile & Start Connecting
              <ArrowRight :size="16" :stroke-width="2" />
            </span>
          </VButton>
        </div>
      </div>

      <!-- Right Decorative Panel — shifted up to account for the glass steps panel -->
      <div class="hidden overflow-visible lg:block">
        <div class="h-full -translate-y-24">
          <OrbitalAnimation />
        </div>
      </div>
    </div>

    <!-- Process Steps — glass bar floating above the animation -->
    <div
      class="steps-section shrink-0 border-t border-white/[0.08] bg-[#162a3a] px-6 py-6 sm:absolute sm:inset-x-0 sm:bottom-0 sm:z-10 sm:bg-[#162a3a]/60 sm:px-10 sm:py-7 sm:backdrop-blur-md sm:backdrop-saturate-150 lg:px-14"
    >
      <!-- Header -->
      <div class="mb-5">
        <div class="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#3b88af]">The process</div>
        <div class="text-xl font-bold text-white sm:text-2xl">Connect in 3 steps</div>
      </div>

      <!-- Cards -->
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div
          v-for="step in STEPS"
          :key="step.number"
          class="step-card rounded-xl border border-white/[0.06] bg-white/[0.04] p-5"
        >
          <!-- Circular Icon -->
          <div class="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[#3b88af]/80">
            <svg
              fill="none"
              stroke="white"
              stroke-width="1.5"
              viewBox="0 0 24 24"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="20"
              height="20"
            >
              <path :d="step.icon" />
            </svg>
          </div>

          <div class="mb-1.5 text-[15px] font-bold text-white">{{ step.number }}. {{ step.title }}</div>
          <div class="text-sm leading-relaxed text-gray-300">{{ step.description }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.welcome-fullbleed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  overflow-y: auto;
}

@media (min-width: 640px) {
  .welcome-fullbleed {
    overflow-y: hidden;
  }
}

/* ─── Left content fadeUp (staggered after rings) ─── */

.title {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.7s both;
}

.subtitle {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.78s both;
}

.cta-wrap {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.86s both;
}

/* ─── Steps staggered entrance ─── */

.steps-section {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 1s both;
}

.step-card:nth-child(1) {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 1.05s both;
}

.step-card:nth-child(2) {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 1.15s both;
}

.step-card:nth-child(3) {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 1.25s both;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .title,
  .subtitle,
  .cta-wrap,
  .steps-section,
  .step-card {
    animation: none;
  }
}
</style>
